import { Image, ITableColumn, Table, TextAlign } from '@busybusy/webapp-react-ui';
import { OrganizationCustomFields } from '__generated__/graphql';
import { correct_large_web, incorrect_large_web } from 'assets/images';
import classNames from 'classnames';
import { useActiveMember, useTableSorting } from 'hooks';
import 'i18nMock';
import { FunctionComponent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ClassName } from 'types/ClassName';
import { getCustomFieldTitles } from 'utils/organizationCustomFieldsUtils';
import { isAdmin } from 'utils/positionUtils';
import { csvEscape } from 'utils/stringUtils';
import { IImportedEquipment } from '../CSVImportEquipmentContainer';
import './CSVImportEquipmentReviewTable.scss';

export interface ICSVImportEquipmentReviewTableProps {
  className?: ClassName;
  onClose?: () => void;
  equipment: IImportedEquipment[];
  loading: boolean;
  lazyLoad: boolean;
  lazyScrollTrigger?: HTMLElement;
  orgCustomFields?: OrganizationCustomFields | null;
}

const CSVImportEquipmentReviewTable: FunctionComponent<ICSVImportEquipmentReviewTableProps> = (props) => {
  const { equipment, loading, lazyLoad, lazyScrollTrigger, orgCustomFields, className } = props;

  const [t] = useTranslation();

  const activeMember = useActiveMember();

  const { sorted, onSort, sortedBy, sortDirection, sortIsDirty } = useTableSorting(equipment);

  function renderSuccessIcon(row: IImportedEquipment, col: any): ReactNode {
    return <Image src={row.success ? correct_large_web : incorrect_large_web} className="p-0" />;
  }

  function getColumns(): Array<ITableColumn<IImportedEquipment>> {
    const customFieldTitles = getCustomFieldTitles(isAdmin(activeMember.position), orgCustomFields);
    const columns: Array<ITableColumn<IImportedEquipment>> = [
      {
        cell: renderSuccessIcon,
        key: 'success',
        align: TextAlign.CENTER,
        cellClassName: 'success-icon',
        headerClassName: 'success-icon',
        size: '30px',
      },
      { key: 'name', sort: true, title: t('Name') },
      { key: 'type', sort: true, title: t('Type') },
      { key: 'category', sort: true, title: t('Category') },
      { key: 'make', sort: true, title: t('Make') },
      { key: 'model', sort: true, title: t('Model') },
      { key: 'year', sort: true, title: t('Year') },
      { key: 'hourMeter', sort: true, title: t('Hour Meter') },
      { key: 'hourlyRate', sort: true, title: t('Hourly Rate') },
    ];

    if (customFieldTitles.length > 0) {
      customFieldTitles.forEach((title, index) => {
        columns.push({
          key: `customField${index + 1}`,
          sort: true,
          title: csvEscape(title),
        });
      });
    }

    return columns;
  }

  const classes = classNames('csv-import-equipment-review-table', className);

  return (
    <Table
      className={classes}
      cols={getColumns()}
      data={sorted}
      minWidth={`${getColumns().length * 240}px`}
      sortDir={sortDirection}
      sortBy={sortedBy}
      lazyScrollSectionSize={50}
      lazyLoad={lazyLoad}
      lazyScrollTrigger={lazyScrollTrigger}
      isLoading={loading}
      strokeCols={false}
      sortIsDirty={sortIsDirty}
      onSort={onSort}
    />
  );
};

export default CSVImportEquipmentReviewTable;
